import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import { smoothScrollToHash } from '@/utils/smoothScroll'
import { initMetaPixel, trackMetaPageView } from '@/utils/metaPixel'
import { clearChunkReloadMarker, isChunkLoadError, reloadForChunkError } from '@/utils/chunkReload'
import {
  canAccessResource,
  canAction,
  isPlatformOperator,
  isWorkspaceOwnerUser,
  resolveDefaultRoute,
} from '@/composables/usePermissions'
import {
  isWorkspaceAdminPath,
  routeBlockedOnTenantHost,
} from '@/utils/planEntitlements'
import { isClinchApexOnlyPublicPath, isTenantWorkspaceHost } from '@/utils/saasPlatform'
import { unknownWorkspaceRedirectUrl } from '@/utils/workspaceHost'

/** The permission a denied route needed, so Forbidden can say what to ask for. */
function deniedPermissionKey(matched) {
  for (const record of matched) {
    const permission = record.meta.permission
    if (permission?.module && permission?.resource) return `${permission.module}.${permission.resource}`
  }
  return ''
}

function routeAllowed(user, meta, routePath = '') {
  if (!user) return false
  if (routePath && routeBlockedOnTenantHost(routePath)) return false
  if (user.is_superuser && !isWorkspaceOwnerUser(user)) return true
  if (meta.workspaceOwnerOk && isWorkspaceOwnerUser(user) && isWorkspaceAdminPath(routePath)) return true
  if (meta.permission) {
    const { module, resource, action = 'read', altResource, altResources } = meta.permission
    const perms = user.permissions
    const alts = [
      ...(altResource ? [altResource] : []),
      ...(Array.isArray(altResources) ? altResources : []),
    ]
    if (canAction(perms, module, resource, action)) return true
    if (canAccessResource(perms, module, resource)) return true
    for (const alt of alts) {
      if (canAction(perms, module, alt, action)) return true
      if (canAccessResource(perms, module, alt)) return true
    }
    return false
  }
  if (meta.requiresAdmin) {
    if (isWorkspaceOwnerUser(user) && isWorkspaceAdminPath(routePath)) return true
    return isPlatformOperator(user)
  }
  return true
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue')
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/Pricing.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue')
  },
  {
    path: '/start',
    name: 'WorkspaceStart',
    component: () => import('../views/WorkspaceStart.vue')
  },
  {
    path: '/start/complete',
    name: 'WorkspaceSignupComplete',
    component: () => import('../views/WorkspaceSignupComplete.vue'),
  },
  {
    path: '/welcome',
    name: 'WorkspaceWelcomeTheme',
    component: () => import('../views/WorkspaceWelcomeTheme.vue'),
  },
  {
    path: '/onboarding',
    name: 'WorkspaceOnboarding',
    component: () => import('../views/onboarding/WorkspaceOnboarding.vue'),
    meta: { requiresAuth: true, workspaceOwnerOk: true, permission: { module: 'workspace', resource: 'onboarding' } },
  },
  {
    path: '/assist-login',
    name: 'AssistLogin',
    component: () => import('../views/AssistLogin.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue')
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('../views/VerifyEmail.vue')
  },
  {
    path: '/issue-verify-email',
    name: 'IssueVerifyEmail',
    component: () => import('../views/IssueVerifyEmail.vue')
  },
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('../views/Blog.vue')
  },
  {
    path: '/businesses',
    name: 'Businesses',
    component: () => import('../views/Businesses.vue')
  },
  {
    path: '/businesses/:id',
    name: 'BusinessDetail',
    component: () => import('../views/BusinessDetail.vue')
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('../views/Products.vue')
  },
  {
    path: '/products/:id',
    name: 'ProductDetail',
    component: () => import('../views/ProductDetail.vue')
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/Contact.vue')
  },
  {
    path: '/workspace-not-found',
    name: 'WorkspaceNotFound',
    component: () => import('../views/WorkspaceNotFound.vue')
  },
  {
    path: '/legal',
    name: 'LegalIndex',
    component: () => import('../views/legal/LegalIndex.vue')
  },
  {
    path: '/legal/:slug',
    name: 'LegalDocument',
    component: () => import('../views/legal/LegalDocument.vue'),
    props: true
  },
  {
    path: '/pay/:paymentId',
    name: 'PaymentSelect',
    component: () => import('../views/PaymentSelect.vue')
  },
  {
    path: '/recover-offer/:token',
    name: 'RecoverOffer',
    component: () => import('../views/RecoverOffer.vue'),
    props: true
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'dashboard', resource: 'overview', action: 'read', altResource: 'business' },
    },
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('../views/Forbidden.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Help & guides: open to every signed-in user; the API filters guides by role.
    path: '/help',
    name: 'HelpHome',
    component: () => import('../views/help/HelpHome.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/help/paths/:key',
    name: 'HelpPath',
    component: () => import('../views/help/HelpPath.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/help/:slug(.+)',
    name: 'HelpArticle',
    component: () => import('../views/help/HelpArticle.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/products',
    name: 'ProductManagement',
    component: () => import('../views/ProductManagement.vue'),
    meta: { requiresAuth: true, permission: { module: 'operations', resource: 'products', action: 'read' } },
  },
  {
    path: '/dashboard/orders',
    name: 'OrderManagement',
    component: () => import('../views/OrderManagement.vue'),
    meta: { requiresAuth: true, permission: { module: 'operations', resource: 'orders', action: 'read' } },
  },
  {
    path: '/dashboard/orders/:id',
    name: 'OrderDetail',
    component: () => import('../views/OrderDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/my-businesses',
    name: 'MyBusinesses',
    component: () => import('../views/MyBusinesses.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/my-business/:id',
    name: 'MyBusiness',
    component: () => import('../views/MyBusiness.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/add-business',
    name: 'AddBusiness',
    component: () => import('../views/AddBusiness.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/edit-business/:id',
    name: 'EditBusiness',
    component: () => import('../views/AddBusiness.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/my-business/:businessId/product/add',
    name: 'AddProduct',
    component: () => import('../views/AddProduct.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/my-business/:businessId/product/:productId',
    name: 'ViewProduct',
    component: () => import('../views/ViewProduct.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/manage/listing-management',
    name: 'AdminListingManagement',
    component: () => import('../views/AdminListingManagement.vue'),
    meta: { requiresAuth: true, permission: { module: 'listing_management', resource: 'business', action: 'read' } },
  },
  {
    path: '/manage/alerts',
    name: 'StoreAlerts',
    component: () => import('../views/admin/StoreAlerts.vue'),
    meta: { requiresAuth: true, permission: { module: 'listing_management', resource: 'store_alerts', action: 'read' } },
  },
  {
    path: '/manage/reporting',
    name: 'ReportingAgent',
    component: () => import('../views/admin/ReportingAgent.vue'),
    meta: { requiresAuth: true, permission: { module: 'workspace', resource: 'ai_reporting' } },
  },
  {
    path: '/manage/appearance',
    name: 'AppearanceSettings',
    component: () => import('../views/admin/AppearanceSettings.vue'),
    meta: { requiresAuth: true, workspaceOwnerOk: true, permission: { module: 'workspace', resource: 'branding' } },
  },
  {
    path: '/manage/clients/new',
    name: 'CreateWorkspaceWizard',
    component: () => import('../views/admin/CreateWorkspaceWizard.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'clients', action: 'create' },
    },
  },
  {
    path: '/manage/clients',
    name: 'TenantRegistry',
    component: () => import('../views/admin/TenantRegistry.vue'),
    meta: {
      requiresAuth: true,
      // Assist (workspace.support) also unlocks the Clients list without Clients read.
      permission: {
        module: 'workspace',
        resource: 'clients',
        action: 'read',
        altResources: ['support'],
      },
    },
  },
  {
    path: '/manage/credit-monitor',
    name: 'CreditMonitor',
    component: () => import('../views/admin/CreditMonitor.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'credit_monitor', action: 'read' },
    },
  },
  {
    path: '/manage/debit-orders',
    name: 'UsageDebitOrders',
    component: () => import('../views/admin/UsageDebitOrders.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'billing_collections', action: 'read' },
    },
  },
  {
    path: '/manage/payment-proofs',
    name: 'EftPaymentProofs',
    component: () => import('../views/admin/EftPaymentProofs.vue'),
    meta: {
      requiresAuth: true,
      permission: {
        module: 'workspace',
        resource: 'billing_collections',
        action: 'read',
        altResources: ['clients', 'support'],
      },
    },
  },
  {
    path: '/manage/whatsapp-usage',
    redirect: { path: '/manage/credit-monitor', query: { tab: 'whatsapp' } },
  },
  {
    path: '/manage/settlements',
    name: 'TenantSettlements',
    component: () => import('../views/admin/TenantSettlements.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'commerce_overview', action: 'read' },
    },
  },
  {
    path: '/manage/packages',
    name: 'Packages',
    component: () => import('../views/admin/Packages.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'plans', action: 'read' },
    },
  },
  {
    path: '/manage/billing-rates',
    name: 'BillingRates',
    component: () => import('../views/admin/BillingRates.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'workspace', resource: 'billing_rates', action: 'read' },
    },
  },
  {
    path: '/manage/subscription',
    name: 'SubscriptionManager',
    component: () => import('../views/admin/SubscriptionManager.vue'),
    meta: { requiresAuth: true, workspaceOwnerOk: true, permission: { module: 'workspace', resource: 'billing' } },
  },
  {
    path: '/manage/settlement',
    name: 'SettlementHub',
    component: () => import('../views/admin/SettlementHub.vue'),
    meta: { requiresAuth: true, workspaceOwnerOk: true, permission: { module: 'workspace', resource: 'settlement' } },
  },
  {
    path: '/manage/store-wallet',
    name: 'StoreWallet',
    component: () => import('../views/admin/StoreWallet.vue'),
    meta: {
      requiresAuth: true,
      workspaceOwnerOk: true,
      permission: { module: 'wallets', resource: 'withdrawals', action: 'read', altResource: 'wallets' },
    },
  },
  {
    path: '/manage/corporate-wallet',
    redirect: (to) => ({
      path: '/manage/subscription',
      query: { ...to.query, tab: 'wallet' },
    }),
  },
  {
    path: '/manage/system',
    name: 'SystemManagement',
    component: () => import('../views/admin/system/SystemManagementHub.vue'),
    meta: {
      requiresAuth: true,
      permission: {
        module: 'system_management',
        resource: 'users',
        action: 'read',
        altResource: 'user_types',
        altResources: ['shopping_agent_mcp', 'api_documentation'],
      },
    },
  },
  {
    path: '/manage/businesses',
    redirect: { path: '/manage/listing-management', query: { tab: 'businesses' } }
  },
  {
    path: '/manage/businesses/',
    redirect: { path: '/manage/listing-management', query: { tab: 'businesses' } }
  },
  {
    path: '/manage/businesses/:id',
    name: 'AdminBusiness',
    component: () => import('../views/MyBusiness.vue'),
    meta: { requiresAuth: true, permission: { module: 'listing_management', resource: 'business', action: 'read' } },
  },
  {
    path: '/manage/businesses/:businessId/product/add',
    name: 'AdminAddProduct',
    component: () => import('../views/AddProduct.vue'),
    meta: { requiresAuth: true, permission: { module: 'operations', resource: 'products', action: 'create' } },
  },
  {
    path: '/manage/businesses/:businessId/product/:productId',
    name: 'AdminViewProduct',
    component: () => import('../views/ViewProduct.vue'),
    meta: { requiresAuth: true, permission: { module: 'operations', resource: 'products', action: 'read' } },
  },
  {
    path: '/manage/categories',
    name: 'CategoryManagement',
    component: () => import('../views/admin/CategoryManagement.vue'),
    meta: { requiresAuth: true, permission: { module: 'listing_management', resource: 'categories', action: 'read' } },
  },
  {
    path: '/manage/promotions',
    name: 'PromotionsCampaigns',
    component: () => import('../views/admin/PromotionsCampaigns.vue'),
    meta: { requiresAuth: true, permission: { module: 'marketing', resource: 'campaigns', action: 'read' } },
  },
  {
    path: '/manage/notifications',
    name: 'NotificationsHub',
    component: () => import('../views/NotificationsHub.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/manage/wallets',
    name: 'AdminWallets',
    component: () => import('../views/admin/AdminWallets.vue'),
    meta: { requiresAuth: true, permission: { module: 'wallets', resource: 'wallets', action: 'read' } },
  },
  {
    path: '/manage/withdrawals',
    name: 'AdminWithdrawals',
    component: () => import('../views/admin/AdminWithdrawals.vue'),
    meta: { requiresAuth: true, permission: { module: 'wallets', resource: 'withdrawals', action: 'read' } },
  },
  {
    path: '/manage/xero-accounting',
    name: 'XeroAccounting',
    component: () => import('../views/admin/XeroAccounting.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'finance', resource: 'ledger', action: 'read' },
    },
  },
  {
    path: '/manage/tickets',
    name: 'ManageTickets',
    component: () => import('../views/admin/ManageTickets.vue'),
    meta: {
      requiresAuth: true,
      permission: {
        module: 'tickets',
        resource: 'issue_tickets',
        action: 'read',
        altResource: 'support_tickets',
      },
    },
  },
  {
    path: '/manage/tickets/:id',
    name: 'AdminTicketDetail',
    component: () => import('../views/admin/AdminTicketDetail.vue'),
    meta: { requiresAuth: true, permission: { module: 'tickets', resource: 'issue_tickets', action: 'read' } },
  },
  {
    path: '/manage/inbox',
    name: 'AdminTeamInbox',
    component: () => import('../views/admin/AdminTeamInbox.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'conversations', action: 'read' } },
  },
  {
    path: '/manage/contacts',
    name: 'AdminInboxContacts',
    component: () => import('../views/admin/InboxContacts.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'conversations', action: 'read' } },
  },
  {
    path: '/manage/company-wabas',
    name: 'AdminCompanyWabas',
    component: () => import('../views/admin/CompanyWabasView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'waba_accounts', action: 'read' } },
  },
  {
    path: '/manage/company-wabas/:id',
    name: 'AdminCompanyWabaDetail',
    component: () => import('../views/admin/CompanyWabaDetailView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'waba_accounts', action: 'read' } },
  },
  {
    path: '/manage/templates',
    name: 'AdminTemplates',
    component: () => import('../views/admin/TemplateManagement.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'templates', action: 'read', altResource: 'broadcasts' } },
  },
  {
    path: '/manage/templates/create',
    name: 'AdminTemplateCreate',
    component: () => import('../views/admin/TemplateEditorView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'templates', action: 'create' } },
  },
  {
    path: '/manage/templates/broadcasts/create',
    name: 'AdminTemplateBroadcastCreate',
    component: () => import('../views/admin/TemplateBroadcastComposeView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'broadcasts', action: 'create' } },
  },
  {
    path: '/manage/templates/broadcasts/:id/analysis',
    name: 'AdminTemplateBroadcastAnalysis',
    component: () => import('../views/admin/TemplateBroadcastAnalysisView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'broadcasts', action: 'read' } },
  },
  {
    path: '/manage/templates/broadcasts/:id',
    name: 'AdminTemplateBroadcastDetail',
    component: () => import('../views/admin/TemplateBroadcastDetailView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'broadcasts', action: 'read' } },
  },
  {
    // The Agent Builder. Opened from the agents table on
    // /manage/system?tab=shopping_agent_mcp. Its five sections are ?tab= query state
    // rather than child routes, matching SystemManagementHub and every other tabbed
    // page here — this router is a flat array with no nested routes anywhere.
    path: '/manage/agents/:id',
    name: 'AdminAgentWorkspace',
    component: () => import('../views/admin/agents/AgentWorkspaceView.vue'),
    meta: {
      requiresAuth: true,
      permission: { module: 'system_management', resource: 'shopping_agent_mcp', action: 'read' },
    },
  },
  {
    path: '/manage/templates/:id',
    name: 'AdminTemplateEdit',
    component: () => import('../views/admin/TemplateEditorView.vue'),
    meta: { requiresAuth: true, permission: { module: 'team_inbox', resource: 'templates', action: 'update' } },
  },
  {
    path: '/manage/support-tickets',
    redirect: (to) => ({
      path: '/manage/tickets',
      query: { ...to.query, tab: 'support' }
    })
  },
  {
    path: '/manage/support-tickets/:id',
    name: 'ManageSupportTicketDetail',
    component: () => import('../views/admin/ManageSupportTicketDetail.vue'),
    meta: { requiresAuth: true, permission: { module: 'tickets', resource: 'support_tickets', action: 'read' } },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      return new Promise((resolve) => {
        const run = () => {
          smoothScrollToHash(to.hash, {
            duration: 1100,
            offset: 100
          }).finally(() => resolve(false))
        }
        // Wait for DOM after route change; double rAF when already on Home for layout settle
        if (to.path === from.path) {
          requestAnimationFrame(() => requestAnimationFrame(run))
        } else {
          setTimeout(run, 200)
        }
      })
    }
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  // Wildcard hosts with no Domain row: leave this host for the platform 404 page.
  if (isTenantWorkspaceHost()) {
    const missingWorkspaceUrl = await unknownWorkspaceRedirectUrl()
    if (missingWorkspaceUrl) {
      next(false)
      window.location.replace(missingWorkspaceUrl)
      return
    }
  }

  // SaaS tenant hosts: Clinch marketing / marketplace stay on the apex domain only.
  if (isTenantWorkspaceHost() && isClinchApexOnlyPublicPath(to.path)) {
    await store.dispatch('ensureAuthInit')
    if (store.getters.isAuthenticated) {
      next(resolveDefaultRoute(store.getters.currentUser))
    } else {
      next({ name: 'Login', query: to.query, replace: true })
    }
    return
  }

  // If user is authenticated and trying to access login/register, redirect to dashboard
  if (
    (to.name === 'Login' || to.name === 'Register' || to.name === 'ForgotPassword' || to.name === 'ResetPassword') &&
    store.getters.isAuthenticated
  ) {
    next(resolveDefaultRoute(store.getters.currentUser))
    return
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    await store.dispatch('ensureAuthInit')
    if (!store.getters.isAuthenticated) {
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
    const user = store.getters.currentUser
    if (routeBlockedOnTenantHost(to.path)) {
      next({ name: 'Forbidden' })
      return
    }
    const denied = to.matched.some(
      (record) =>
        (record.meta.permission || record.meta.requiresAdmin) &&
        !routeAllowed(user, record.meta, to.path)
    )
    if (denied) {
      const need = deniedPermissionKey(to.matched)
      next({ name: 'Forbidden', query: need ? { need } : {} })
      return
    }
    next()
  } else {
    next()
  }
})

initMetaPixel()

router.afterEach(() => {
  clearChunkReloadMarker()
  trackMetaPageView()
})

router.onError((error, to) => {
  if (!isChunkLoadError(error)) return
  console.warn('[router] page chunk failed to load, reloading', to?.fullPath, error)
  reloadForChunkError(to?.fullPath)
})

export default router



